import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { productApi, categoryApi } from '../../services/productApi';
import { useLocation, useNavigate } from "react-router-dom";
import Button from "../../components/Button";
import Pagination from "../../components/Pagination";
import { getFallbackImage } from "../../utils/getFallbackImage";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { FiDownload, FiX  } from "react-icons/fi";
import { exportToExcel } from "../../utils/exportToExcel";

const SupplierProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // form states
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [status, setStatus] = useState('');

  // applied filter states
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [appliedStartDate, setAppliedStartDate] = useState(null);
  const [appliedEndDate, setAppliedEndDate] = useState(null);
  const [appliedStatus, setAppliedStatus] = useState('');

  // pagination states
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const location = useLocation();
  const navigate = useNavigate();

  const [ordering, setOrdering] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get("ordering") || "-created_at";
  });

  const SORT_OPTIONS = [
    { label: "جدیدترین", value: "-created_at" },
    { label: "قدیمی‌ترین", value: "created_at" },
    { label: "جدیدترین بازبینی", value: "-reviewed_at" },
    { label: "قدیمی‌ترین بازبینی", value: "reviewed_at" },
  ];

  const STATUS_CHOICES = [
    { value: '', label: 'همه کالاها' },
    { value: 'draft', label: 'پیشنویس' },
    { value: 'pending', label: 'در انتظار تأئید' },
    { value: 'editing', label: 'نیاز به اصلاح' },
    { value: 'approved', label: 'تأئید شده' },
    { value: 'rejected', label: 'رد شده' },
  ];

  const UNIT_TRANSLATIONS = {
    'count': 'عدد',
    'package': 'بسته',
    'weight': 'کیلو گرم',
    'length': 'متر',
    'area': 'متر مربع',
    'litr': 'لیتر',
    'volume': 'متر مکعب'
  };

  const toGregorianISO = (date) => {
    if (!date) return '';
    return date.toDate().toISOString();
  };

  const buildProductQueryParams = useCallback((pageValue = page, noPagination = false) => {
    const params = new URLSearchParams();

    if (!noPagination) {
      params.append('page', pageValue);
      params.append('page_size', pageSize);
    }

    params.append('no_pagination', String(noPagination));

    if (appliedSearchTerm) params.append('search', appliedSearchTerm);
    if (appliedStatus) params.append('status', appliedStatus);
    if (appliedStartDate) params.append('start_date', toGregorianISO(appliedStartDate));
    if (appliedEndDate) params.append('end_date', toGregorianISO(appliedEndDate));
    if (ordering) params.append('ordering', ordering);

    return params;
  }, [
    page,
    pageSize,
    appliedSearchTerm,
    appliedStatus,
    appliedStartDate,
    appliedEndDate,
    ordering,
  ]);

  const fetchProducts = useCallback(async (pageValue = page, noPagination = false) => {
    setLoading(true);
    setError(null);

    try {
      const params = buildProductQueryParams(pageValue, noPagination);
      const response = await productApi.getList(`?${params.toString()}`);
      const responseData = response.data;

      if (Array.isArray(responseData)) {
        setProducts(responseData);
        setTotalCount(responseData.length);
      } else {
        setProducts(responseData.results || []);
        setTotalCount(responseData.count || 0);
      }
    } catch (error) {
      if (error.code === 'NO_INTERNET') {
        setError(error.message);
        return;
      }

      setError('قطع اینترنت');
    } finally {
      setLoading(false);
    }
  }, [buildProductQueryParams, page]);

  const handleExportExcel = async () => {
    try {
      const params = buildProductQueryParams(1, true);
      const response = await productApi.getExportExcelList(`?${params.toString()}`);
      const data = Array.isArray(response.data)
        ? response.data
        : response.data.results || [];

      if (!data.length) {
        alert("داده‌ای برای خروجی وجود ندارد");
        return;
      }

      const excelData = data.map((product) => ({
        id: product.product_code ?? "",
        name: product.name ?? "",
        slug: product.slug ?? "",
        brand: product.brand ?? "",
        category_name: product.category_name ?? "",
        status: product.status ?? "",
        is_active: product.is_active ?? true,
        created_at: product.created_at ? formatDate(product.created_at) : "",
        system_attributes_count: product.system_attributes_count ?? 0,
        custom_attributes_count: product.custom_attributes_count ?? 0,
        images_count: product.images_count ?? 0,
        unit_of_measure: UNIT_TRANSLATIONS[product.unit_of_measure] ?? "",
        weight: product.weight ?? 0,
        width: product.width ?? 0,
        length: product.length ?? 0,
        height: product.height ?? 0,
        delivery_by_supplier: product.delivery_by_supplier ?? true,
        delivery_by_customer: product.delivery_by_customer ?? true,
        delivery_by_carrier: product.delivery_by_carrier ?? true,
        is_packable: product.is_packable ?? true,
        is_fragile: product.is_fragile ?? false,
        is_flammable: product.is_flammable ?? false,
        is_liquid: product.is_liquid ?? false,
        is_corrosive: product.is_corrosive ?? false,
      }));

      exportToExcel(excelData, "کالاهای-تامین-کننده", {
        headers: {
          id: "کد یکتا",
          name: "نام کالا",
          slug: "اسلاگ کالا",
          brand: "برند",
          category_name: "دسته",
          status: "وضعیت",
          is_active: "فعال",
          created_at: "تاریخ ساخت",
          system_attributes_count:"تعداد مشخصه سیستمی",
          custom_attributes_count:"تعداد مشخصه سفارشی",
          images_count:"تعداد عکس",
          unit_of_measure:"واحد اندازه گیری",
          weight:"وزن",
          width:"عرض",
          length:"طول",
          height:"ارتفاع",
          delivery_by_supplier:"ارسال تأمین کننده",
          delivery_by_customer:"تحویل مشتری",
          delivery_by_carrier:"ارسال شرکت حمل",
          is_packable:"قابل بسته بندی",
          is_fragile:"شکستنی",
          is_flammable:"اشتعال پذیر",
          is_liquid:"مایعات",
          is_corrosive:"خورنده",
        },
      });
    } catch (err) {
      console.error(err);
      alert("خطا در دریافت اطلاعات کالاها");
    }
  };

  const fetchCategories = useCallback(async () => {
    try {
      const response = await categoryApi.getLeaves();
      setCategories(response.data);
    } catch (error) {
      console.error('خطا در دریافت دسته‌ها:', error);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const orderingValue = params.get("ordering") || "-created_at";
    setOrdering(orderingValue);
  }, [location.search]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchProducts(page, false);
  }, [fetchProducts, page]);

  const totalPages = Math.ceil(totalCount / pageSize);

  const renderStatusBadge = (statusValue) => {
    const statusConfig = {
      draft: { label: 'پیش‌نویس', color: 'bg-gray-100 text-gray-800' },
      pending: { label: 'در انتظار تأیید', color: 'bg-yellow-100 text-yellow-800' },
      approved: { label: 'تأیید شده', color: 'bg-green-100 text-green-800' },
      rejected: { label: 'رد شده', color: 'bg-red-100 text-red-800' },
      editing: { label: 'نیاز به اصلاح', color: 'bg-blue-100 text-blue-800' }
    };

    const config = statusConfig[statusValue] || {
      label: statusValue,
      color: 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  const handleOrderingChange = useCallback(
    (value) => {
      setOrdering(value);
      setPage(1);

      const params = new URLSearchParams(location.search);
      params.set("ordering", value);

      navigate({
        pathname: location.pathname,
        search: params.toString(),
      });
    },
    [location.search, location.pathname, navigate]
  );

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusSearch = (e) => {
    setStatus(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();

    setAppliedSearchTerm(searchTerm);
    setAppliedStatus(status);
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStartDate(null);
    setEndDate(null);
    setStatus('');

    setAppliedSearchTerm('');
    setAppliedStartDate(null);
    setAppliedEndDate(null);
    setAppliedStatus('');
    setPage(1);
  };

  return (
    <div className="p-6">
      <form
        onSubmit={handleSearchSubmit}
        className="w-full my-6 mr-[8px]"
      >
        <div
            className="
            grid grid-cols-1 gap-3
            sm:grid-cols-2
            lg:grid-cols-[2fr_1fr_1fr_auto_auto]
            items-center
          "
        >
          <input
              type="text"
              value={searchTerm}
              onChange={handleSearch}
              placeholder="جستجو بر اساس نام، کد، برند، تأمین‌کننده..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg
            focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />

          <select
              name="status"
              value={status}
              onChange={handleStatusSearch}
              className="border rounded-lg p-2.5 pr-[8px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] transition-all"
          >
            {STATUS_CHOICES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
            ))}
          </select>

          <DatePicker
              value={startDate}
              onChange={setStartDate}
              calendar={persian}
              locale={persian_fa}
              placeholder="از تاریخ"
              inputClass="
              w-full border rounded-lg px-3 py-2
              focus:ring-2 focus:ring-blue-500
            "
          />

          <DatePicker
              value={endDate}
              onChange={setEndDate}
              calendar={persian}
              locale={persian_fa}
              placeholder="تا تاریخ"
              inputClass="
              w-full border rounded-lg px-3 py-2
              focus:ring-2 focus:ring-blue-500
            "
          />

          <div className="flex items-center gap-2 w-full lg:w-auto">
            <Button type="submit" className="w-full lg:w-auto">
              جستجو
            </Button>

            {(searchTerm || startDate || endDate || status) && (
                <button
                    type="button"
                    onClick={handleClearFilters}
                    title="پاک کردن فیلترها"
                    className="h-10 w-10 flex items-center justify-center rounded-lg border border-gray-300 text-gray-500 hover:text-red-600 hover:border-red-400 hover:bg-red-50 transition-all"
                >
                  <FiX size={18} />
                </button>
            )}
          </div>

        </div>
      </form>

      <div className="mb-[16px] flex items-center gap-2 border-b pb-4">
        {SORT_OPTIONS.map((option) => (
            <button
                key={option.value}
                type="button"
                onClick={() => handleOrderingChange(option.value)}
                className={`px-3 py-1 text-sm rounded-md border transition ${
                    ordering === option.value
                        ? "bg-[#F5F5F5] text-[#1E3A5F] font-semibold border-[#1E3A5F]"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between mr-4 mb-6">
        <Button
          variant="primary"
          children="+ ساخت کالا جدید"
          onClick={() => navigate("/supplier-panel/supplier-product-create/")}
        />

        <Button
          variant="secondary"
          onClick={handleExportExcel}
          icon={<FiDownload className="w-5 h-5" />}
          children="خروجی اکسل"
        />
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="mt-2 text-gray-600">در حال دریافت محصولات...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-600">
            {error}
          </div>
        ) : products.length === 0 ? (
          <div className="p-8 text-center">
            <svg className="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
              />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">محصولی یافت نشد</h3>
            <p className="mt-1 text-gray-500">با تغییر فیلترها دوباره امتحان کنید.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    تصویر
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    نام محصول
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    دسته
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    برند
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    تاریخ ساخت
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    وضعیت
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    عملیات
                  </th>
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-gray-200">
                {products.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="w-12 h-12 rounded overflow-hidden border border-gray-200">
                          {product.featured_thumbnail_url || product.featured_image_url ? (
                              <img
                                  src={product.featured_thumbnail_url || product.featured_image_url}
                                  alt={product.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = getFallbackImage();
                                  }}
                              />
                          ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor"
                                     viewBox="0 0 24 24">
                                  <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={1}
                                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                              </div>
                          )}
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium text-gray-900">{product.name}</div>

                          {product.is_active === false && (
                              <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                                غیر فعال
                              </span>
                          )}
                        </div>
                      </td>


                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {product.category_name || 'تعیین نشده'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {product.brand || 'تعیین نشده'}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(product.created_at)}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        {renderStatusBadge(product.status)}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2 space-x-reverse">
                          <Link
                              to={`/supplier-panel/supplier-product/${product.id}`}
                              className="text-blue-600 hover:text-blue-900"
                              title="مشاهده جزئیات"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                            </svg>
                          </Link>

                          <Link
                              to={`/supplier-panel/supplier-product-edit/${product.id}`}
                              className="text-green-600 hover:text-green-900"
                              title="ویرایش"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                            </svg>
                          </Link>

                        </div>
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {products.length > 0 && (
          <div className="mt-6">
            <div className="mb-4 text-center text-sm text-gray-700">
              نمایش <span className="font-medium">{products.length}</span> محصول از{' '}
              <span className="font-medium">
              {new Intl.NumberFormat('fa-IR').format(totalCount)}
            </span>
          </div>

          <div className="flex justify-center">
            <Pagination
              page={page}
              setPage={setPage}
              totalPages={totalPages}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierProductManagement;
